import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PickupInfo = ({ 
  pickupWindow = {},
  location = {},
  businessHours = {},
  policies = {}
}) => {
  const [showDirections, setShowDirections] = useState(false);
  
  const {
    startTime = '',
    endTime = '',
    timeRemaining = '',
    urgencyLevel = 'low'
  } = pickupWindow;

  const {
    address = '',
    coordinates = { lat: 19.4326, lng: -99.1332 },
    walkingDistance = '',
    walkingTime = '',
    landmarks = []
  } = location;

  const {
    openTime = '',
    closeTime = '',
    isOpen = false,
    specialHours = ''
  } = businessHours;

  const {
    readyOnArrival = true,
    maxWaitTime = '3 minutos',
    cancellationPolicy = '',
    qualityGuarantee = ''
  } = policies;

  const getUrgencyColor = () => {
    switch (urgencyLevel) {
      case 'high':
        return 'text-error';
      case 'medium':
        return 'text-warning';
      default:
        return 'text-success';
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return new Date(`2000-01-01T${timeString}`)?.toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h3 className="font-heading font-semibold text-lg text-foreground mb-4">
        Información de Recogida
      </h3>
      {/* Pickup Time Window */}
      <div className="space-y-4 mb-6">
        <div className="bg-muted/50 p-4 rounded-md">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-foreground">Ventana de recogida</h4>
            <div className="flex items-center space-x-1">
              <Icon name="Clock" size={16} className={getUrgencyColor()} />
              <span className={`text-sm font-medium ${getUrgencyColor()}`}>
                {timeRemaining}
              </span>
            </div>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Icon name="Calendar" size={14} />
              <span>Hoy</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Clock" size={14} />
              <span>{formatTime(startTime)} - {formatTime(endTime)}</span>
            </div>
          </div>
        </div>

        {/* Ready on Arrival Policy */}
        {readyOnArrival && (
          <div className="bg-success/10 border border-success/20 p-3 rounded-md">
            <div className="flex items-start space-x-2">
              <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5" />
              <div>
                <p className="text-sm font-medium text-success mb-1">
                  Política "Listo al llegar"
                </p>
                <p className="text-xs text-success/80">
                  Tu pedido estará listo cuando llegues. Tiempo máximo de espera: {maxWaitTime}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Location Information */}
      <div className="space-y-4 mb-6">
        <h4 className="font-medium text-foreground">Ubicación</h4>
        
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <Icon name="MapPin" size={16} color="var(--color-primary)" className="mt-1" />
            <div className="flex-1">
              <p className="text-sm text-foreground font-medium">{address}</p>
              <div className="flex items-center space-x-4 mt-1 text-xs text-muted-foreground">
                <span>{walkingDistance}</span>
                <span>{walkingTime} caminando</span>
              </div>
            </div>
          </div>

          {/* Landmarks */}
          {landmarks?.length > 0 && (
            <div className="ml-7">
              <p className="text-xs text-muted-foreground mb-1">Puntos de referencia:</p>
              <div className="space-y-1">
                {landmarks?.map((landmark, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Icon name="Navigation" size={12} color="var(--color-muted-foreground)" />
                    <span className="text-xs text-muted-foreground">{landmark}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Directions Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDirections(!showDirections)}
            className="w-full mt-3"
          >
            <Icon name="Navigation" size={16} className="mr-2" />
            {showDirections ? 'Ocultar mapa' : 'Ver direcciones'}
          </Button>

          {/* Map */}
          {showDirections && (
            <div className="mt-3 h-48 bg-muted rounded-md overflow-hidden">
              <iframe
                width="100%"
                height="100%"
                loading="lazy"
                title="Ubicación del negocio"
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps?q=${coordinates?.lat},${coordinates?.lng}&z=16&output=embed`}
                className="border-0"
              />
            </div>
          )}
        </div>
      </div>
      {/* Business Hours */}
      <div className="space-y-3 mb-6">
        <h4 className="font-medium text-foreground">Horarios del negocio</h4>
        
        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-md">
          <div className="flex items-center space-x-2">
            <Icon 
              name={isOpen ? 'Clock' : 'ClockOff'} 
              size={16} 
              color={isOpen ? 'var(--color-success)' : 'var(--color-error)'}
            />
            <span className="text-sm font-medium text-foreground">
              {isOpen ? 'Abierto ahora' : 'Cerrado'}
            </span>
          </div>
          
          <span className="text-sm text-muted-foreground">
            {formatTime(openTime)} - {formatTime(closeTime)}
          </span>
        </div>

        {specialHours && (
          <p className="text-xs text-muted-foreground bg-warning/10 p-2 rounded-md">
            <Icon name="Info" size={12} className="inline mr-1" />
            {specialHours}
          </p>
        )}
      </div>
      {/* Policies */}
      <div className="space-y-3">
        <h4 className="font-medium text-foreground">Políticas</h4>
        
        <div className="space-y-2 text-sm text-muted-foreground">
          {cancellationPolicy && (
            <div className="flex items-start space-x-2">
              <Icon name="RefreshCw" size={14} className="mt-0.5" />
              <div>
                <span className="font-medium text-foreground">Cancelación: </span>
                {cancellationPolicy}
              </div>
            </div>
          )}
          
          {qualityGuarantee && (
            <div className="flex items-start space-x-2">
              <Icon name="Shield" size={14} className="mt-0.5" />
              <div>
                <span className="font-medium text-foreground">Garantía: </span>
                {qualityGuarantee}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PickupInfo;